import React, { useEffect, useRef, useState } from "react";
import locationMap from "assets/images/location_intelligence_map.png";
import { ReactComponent as NetworkWiredIcon } from "assets/icons/network_wired.svg";
import { ReactComponent as CloseIcon } from "assets/icons/close.svg";
import { ReactComponent as LocationIcon } from "assets/icons/location.svg";
import { ReactComponent as InfoIcon } from "assets/icons/info_2.svg";
import AnimatedDialogLayout from "components/Dialog/AnimatedDialogLayout";
import L, { DivIcon } from "leaflet";
import locationPin from "assets/icons/location_pin.svg";
import subwayPin from "assets/images/metropolitane_logo.png";
import { MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import ProgressBar from "components/Loading/ProgressBar";
import useAuth from "hooks/useAuth";
import useProperties from "hooks/useProperties";

const transportModes = [
  { mode: "walk", label: "A piedi" },
  { mode: "bike", label: "In Bici" },
  { mode: "car", label: "In Auto" },
  { mode: "transit", label: "Mezzi Pubblici" },
];

const LocationIntelligencePopup = ({ property }) => {
  const { openLocationIntelligencePopup, toggleLocationItelligencePopup } =
    useProperties();

  const [travelMode, setTravelMode] = useState("walk");
  const [travelTime, setTravelTime] = useState(15);
  const [isLoading, setIsLoading] = useState(false);
  const [client, setClient] = useState(null);
  const mapRef = useRef(null);
  const polygonOverlayLayer = useRef(null);
  const subwayLayer = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const callInProgress = useRef(false);
  const center = [
    property.coordinates.coordinates[0], // Longitude
    property.coordinates.coordinates[1], // Latitude
  ];
  const sources = [{ lng: center[0], lat: center[1], id: 1 }];

  useEffect(() => {
    if (typeof window !== "undefined" && window.tgm) {
      const targomoClient = new window.tgm.TargomoClient(
        "westcentraleurope",
        process.env.REACT_APP_TARGOMO_API_KEY
      );
      setClient(targomoClient);
    } else {
      console.error("TargomoClient is not available.");
    }
  }, []);

  const colorData = {
    300: "#1DA1F2",
    600: "#1DA1F2",
    900: "#1DA1F2",
    1200: "#1DA1F2",
    1500: "#1DA1F2",
    1800: "#1DA1F2",
    2100: "#1DA1F2",
    2400: "#1DA1F2",
    2700: "#1DA1F2",
    3000: "#1DA1F2",
    3300: "#1DA1F2",
    3600: "#1DA1F2",
  };

  // Fetch polygons based on the current travel mode and time
  const fetchPolygons = async () => {
    setIsLoading(true);
    try {
      const options = {
        travelType: travelMode,
        travelEdgeWeights: [travelTime * 60], // Convert into seconds
        maxEdgeWeight: 3600,
        edgeWeight: "time",
        serializer: "json",
      };

      const polygons = await client.polygons.fetch(sources, options);

      polygonOverlayLayer.current.setData(polygons); // Update data

      // Fit map to polygon bounds
      const bounds = polygons.getMaxBounds();
      mapRef.current.fitBounds(
        new L.latLngBounds(bounds.northEast, bounds.southWest)
      );
    } catch (error) {
      console.error("Error fetching polygons:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSubwayStations = async () => {
    setIsLoading(true);
    try {
      const subwayOptions = {
        travelType: travelMode,
        travelEdgeWeights: [travelTime * 60], // Convert into seconds
        maxEdgeWeight: travelTime * 60,
        edgeWeight: "time",
        format: "geojson",
        osmTypes: [{ key: "railway", value: "subway_entrance" }],
      };

      const subwayStations = await client.pois.reachable(
        sources[0],
        subwayOptions
      );

      // Ensure subwayLayer is initialized and added to the map
      if (!subwayLayer.current) {
        subwayLayer.current = L.layerGroup().addTo(mapRef.current); // Initialize and add to map if not already done
      }

      // Clear previous markers
      subwayLayer.current.clearLayers();

      subwayStations.features.forEach((station) => {
        const { geometry, properties } = station;
        const latLng = [geometry.coordinates[1], geometry.coordinates[0]];

        // Calculate travel time in minutes
        const estimatedTravelTime = Math.max(
          1, // Never less than 1 minute
          Math.round((station.properties.edgeWeight / 60) * 10) / 10
        );

        const timeLabel = estimatedTravelTime === 1 ? "Minuto" : "Minuti";

        const marker = L.marker(latLng, {
          icon: new DivIcon({
            html: `
              <div class="subway_station">
               <img src="${subwayPin}" width="36" height="36" alt="Subway" style='width:24px; height:24px' />
              </div>`,
            iconSize: [36, 36],
            className: "",
          }),
        }).addTo(subwayLayer.current);

        marker.bindPopup(
          `<p style="font-weight:500; color:#9ca3af; margin:10px 0px; ">Ingresso Metro: <span style="color: #1DA1F2">${
            properties.tags.name
          }</span></p>
          <p style="font-weight:500; color:#9ca3af; margin:10px 0px;">Tempo Stimato - ${
            transportModes.find((elm) => elm.mode === travelMode).label
          }: <span style="color: #1DA1F2">${Math.round(
            estimatedTravelTime
          )} ${timeLabel}</span></p>`
        );
      });
    } catch (error) {
      console.error("Error fetching subway stations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const UpdateMap = () => {
    useEffect(() => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Initialize polygon overlay layer once
      if (!polygonOverlayLayer.current) {
        polygonOverlayLayer.current =
          new window.tgm.leaflet.TgmLeafletPolygonOverlay({
            strokeWidth: 20,
            zIndex: 1,
            colors: colorData,
          });
        polygonOverlayLayer.current.addTo(mapRef.current); // Add once to the map
      }

      // Add markers for the sources on the map.
      var leafIcon = new DivIcon({
        html: `
          <div class="">
           <img src="${locationPin}" width="38" height="38" alt="Location Pin" />
          </div>
        `,
        iconSize: [38, 38],
        className: "",
      });

      sources.forEach((source) => {
        L.marker([source.lat, source.lng], { icon: leafIcon }).addTo(
          mapRef.current
        );
      });

      debounceTimeoutRef.current = setTimeout(async () => {
        if (callInProgress.current) {
          return;
        }

        callInProgress.current = true;
        setIsLoading(true);

        try {
          await Promise.all([fetchPolygons(), fetchSubwayStations()]);
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setIsLoading(false);
          callInProgress.current = false;
        }
      }, 500);

      return () => {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
      };
    }, [travelMode, travelTime, openLocationIntelligencePopup]);

    return null;
  };
  return (
    <>
      <AnimatedDialogLayout
        isOpen={openLocationIntelligencePopup === property?._id}
        onClose={() => toggleLocationItelligencePopup(property?._id)}
        styleDropdown={{ height: "100%", maxWidth: 1037, width: "100%" }}
      >
        <div className="h-full flex flex-col">
          <div className="flex mt-1 items-center justify-between">
            <h2 className="text-[22px] font-bold leading-[28px]">
              Location Intelligence
            </h2>
            <CloseIcon
              onClick={() => toggleLocationItelligencePopup(property?._id)}
              className="w-4 h-4 cursor-pointer"
            />
          </div>
          <div className="relative h-full flex-grow mt-5">
            {isLoading && <ProgressBar loading={isLoading} />}
            <MapContainer
              ref={mapRef}
              center={center}
              zoomControl={false}
              zoom={12}
              style={{
                width: "100%",
                height: "100%",
                borderRadius: 20,
                zIndex: 1,
              }}
            >
              <TileLayer
                attribution='Data by &copy; <a href="http://openstreetmap.org">OpenStreetMap</a>, under <a href="http://www.openstreetmap.org/copyright">ODbL</a>'
                url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}.png"
              />
              <TileLayer
                attribution='Data by &copy; <a href="http://openstreetmap.org">OpenStreetMap</a>, under <a href="http://www.openstreetmap.org/copyright">ODbL</a>'
                url="https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}.png"
              />
              <Marker
                position={center}
                icon={
                  new DivIcon({
                    html: `
                  <div class="">
                   <img src="${locationPin}" width="38" height="38" alt="Location Pin" />
                  </div>
                `,
                    iconSize: [38, 38],
                    className: "",
                  })
                }
                zIndexOffset={1000}
              />

              <UpdateMap />
            </MapContainer>

            <div className="bg-white pl-5 pb-5 pr-3 pt-3 z-10 rounded-[11px] absolute top-[17px] left-[17px] right-[17px] sm:right-auto ">
              <h3 className="text-[16px] font-semibold">
                Location Intelligence
              </h3>
              <div className="mt-1 sm:mt-3">
                <p>
                  Tempo di Viaggio: <strong>{travelTime} min</strong>
                </p>
                <input
                  type="range"
                  min={5}
                  max={60}
                  step={5}
                  value={travelTime}
                  onChange={(e) => setTravelTime(e.target.value)}
                  className="mt-[2px] sm:mt-2 input_range cursor-pointer bg-slate-100 w-full hover:bg-none border-none outline-none "
                />
              </div>
              <div className="mt-1 sm:mt-3">
                <p>Modalità di Trasporto:</p>
                <div className="mt-[2px] sm:mt-2 flex items-center space-x-2 w-full overflow-x-auto">
                  {transportModes.map(({ mode, label }) => (
                    <div
                      key={mode}
                      onClick={() => {
                        setTravelMode(mode);
                      }}
                      className={`border min-w-fit rounded-[5px] p-2 sm:p-4 cursor-pointer w-full ${
                        travelMode === mode ? "bg-yellow-200" : ""
                      }`}
                    >
                      <p className="flex-shrink-0 text-nowrap">{label}</p>
                      <p className="font-semibold flex-shrink-0">
                        {travelTime} min
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div
              onClick={() =>
                window.open(
                  `https://www.google.com/maps?q=${sources[0].lat},${sources[0].lng}`,
                  "_blank"
                )
              }
              className="border-[#F0EFFB] hidden sm:inline-flex z-10 absolute bg-white cursor-pointer right-[17px] top-[17px] rounded-[9px] border items-center space-x-3 h-[59px] pl-[15px] pr-[20px]"
            >
              <LocationIcon className="w-[18px] h-[24px]" />
              <p className="text-[20px] font-normal text-black">Vedi su Maps</p>
            </div>
          </div>
        </div>
      </AnimatedDialogLayout>
    </>
  );
};

export default LocationIntelligencePopup;
